(ns minorodata.components.admin.byol
  (:require ["lucide-react" :refer [CheckCircle2 Eye EyeOff KeyRound SlidersHorizontal]]
            [applied-science.js-interop :as js-interop]
            [clojure.string :as string]
            [minorodata.ai-chat.byol :as byol]
            [minorodata.app.handlers :refer [dispatch]]
            [minorodata.app.posthog-events :as posthog-events]
            [minorodata.app.utils :as app.utils]
            [minorodata.components.forms.inputs :as inputs]
            [re-posh.core :as re-posh]
            [react :as react]
            [ui.alert :refer [alert]]
            [ui.button :refer [button]]
            [ui.chip :refer [chip]]
            [ui.dialog :as dialog]
            [ui.drawer :as drawer]
            [ui.fields :as fields]
            [ui.typography :as typography]))


(defn- save-setting [settings local-changes api-changes & [capture]]
  (when (seq settings)
    (dispatch [:ai-settings/update local-changes {:replace true}]))
  (dispatch [:ai-chat/update-settings api-changes capture]))


(defn- option-value [options value]
  (if (some #(= value (:value %)) options) value ""))


(defn- credential-meta [{:keys [label last4 last-validated-at]}]
  (string/join " · "
               (remove nil?
                       [label
                        (when last4 (str "•••• " last4))
                        (when last-validated-at
                          (str "validated " (app.utils/format-date last-validated-at)))])))


(defn- status-chip [credential]
  (cond
    (nil? credential)            [chip {} "Not configured"]
    (byol/validated? credential) [chip {:variant :success} "Validated"]
    :else                        [chip {:variant :error} "Validation failed"]))


(defn- provider-row [{:keys [provider credential loaded? byol-enabled? on-add on-remove]}]
  [:div {:class "flex items-center gap-4 py-3"}
   [:div {:class "flex-1 font-medium"} (:name provider)]
   [:div {:class "flex-1 flex flex-col gap-1 items-start"}
    (if loaded?
      [status-chip credential]
      [chip {} "Not loaded"])
    (when credential
      [:span {:class "text-xs text-kleene-text-secondary"} (credential-meta credential)])]
   (when loaded?
     [:div {:class "flex gap-2"}
      [button {:variant  :link
               :size     :small
               :disabled (not byol-enabled?)
               :on-click on-add}
       (if credential "Replace key" "Add key")]
      (when credential
        [button {:variant  :link
                 :size     :small
                 :on-click on-remove}
         "Remove"])])])


(defn- credential-drawer [{:keys [provider replace? on-close]}]
  (let [[mode set-mode]   (react/useState "api_key")
        [value set-value] (react/useState "")
        [label set-label] (react/useState "")
        [file set-file]   (react/useState nil)
        [show? set-show]  (react/useState false)
        saving?           @(re-posh/subscribe [:mold-http/loading? :ai-chat/save-provider-credential])
        result            (let [r @(re-posh/subscribe [:ai-chat/credential-save-result])]
                            (when (= provider (:provider r)) r))
        google?           (= "google" provider)
        vertex?           (and google? (= "vertex_sa_json" mode))
        sa-json           (when vertex? (byol/parse-service-account (:content file)))
        ready?            (if vertex? (some? sa-json) (not (string/blank? value)))
        saved?            (= :ok (:status result))
        switch-mode       (fn [new-mode]
                            (set-value "")
                            (set-file nil)
                            (dispatch [:ai-chat/clear-credential-save-result])
                            (set-mode new-mode))
        submit            (fn []
                            (let [key-type (if vertex? "vertex_sa_json" "api_key")]
                              (dispatch [:posthog/capture {:event (:byol/key-submitted posthog-events/events)
                                                           :props {:provider provider
                                                                   :action   (if replace? "replace" "add")
                                                                   :key_type key-type}}])
                              (dispatch [:ai-chat/save-provider-credential
                                         {:provider provider
                                          :type     key-type
                                          :value    (if vertex? sa-json value)
                                          :label    label}])))]
    (react/useEffect
     (fn []
       (dispatch [:ai-chat/clear-credential-save-result])
       (fn []))
     #js [])
    (react/useEffect
     (fn []
       (when saved?
         (set-value "")
         (set-file nil))
       js/undefined)
     #js [saved?])

    [drawer/drawer
     {:open           true
      :on-open-change (fn [open?] (when (and (not open?) (not saving?)) (on-close)))
      :x-close        (not saving?)
      :title          (str (if replace? "Replace " "Add ") (byol/provider-name provider) " key")
      :content
      [:div {:class "flex flex-col gap-4 max-w-md"}
       (when google?
         [:div {:class "flex gap-2"}
          [button {:variant  (if vertex? :secondary :primary)
                   :size     :small
                   :disabled saved?
                   :on-click #(switch-mode "api_key")}
           "API key"]
          [button {:variant  (if vertex? :primary :secondary)
                   :size     :small
                   :disabled saved?
                   :on-click #(switch-mode "vertex_sa_json")}
           "Vertex service account JSON"]])

       (if vertex?
         [:div
          (when-not saved?
            [inputs/file-input {:id    :service-account
                                :field {:value  file
                                        :action set-file}}])
          (when (and file (nil? sa-json))
            [:p {:class "mt-2 text-xs text-kleene-error-01"} "This file is not valid JSON."])
          [:p {:class "mt-2 text-xs text-kleene-text-secondary"}
           "Runs in Google's EU region. Use a service account created only for Kleene with Vertex AI access."]]

         [:div {:class "flex items-end gap-2"}
          [fields/input {:id          :provider-key
                         :label       "API key"
                         :type        (if show? "text" "password")
                         :value       value
                         :placeholder (get byol/key-placeholders provider)
                         :disabled    saved?
                         :input-props {:auto-complete "new-password"}
                         :on-change   #(set-value (js-interop/get-in % [:target :value]))}]
          [button {:variant  :secondary
                   :icon     (if show? EyeOff Eye)
                   :title    (if show? "Hide key" "Show key")
                   :on-click #(set-show (not show?))}]])

       [fields/input {:id          :provider-key-label
                      :label       "Label"
                      :value       label
                      :placeholder "e.g. prod project key"
                      :disabled    saved?
                      :on-change   #(set-label (js-interop/get-in % [:target :value]))}]

       [:p {:class "text-xs text-kleene-text-secondary"}
        "Use a dedicated key with the least access you can: an OpenAI project key, an Anthropic workspace key, or a Google service account created only for Kleene."]

       (case (:status result)
         :ok     [alert {:variant :success :icon CheckCircle2 :title "Validated and saved"}
                  (str (when (:last4 result) (str "Key ending in " (:last4 result) ". "))
                       (let [n (count (:models result))]
                         (case n
                           0 "The provider returned no models, so it cannot be made the default yet. "
                           1 "1 model available. "
                           (str n " models available. ")))
                       "The key will not be shown again.")]
         :failed [alert {:variant :error :title "Not saved"}
                  (:message result)]
         nil)

       [:div {:class "flex justify-end gap-2 pt-4 border-t"}
        (if saved?
          [button {:on-click on-close} "Done"]
          [:<>
           [button {:variant :secondary :disabled saving? :on-click on-close} "Cancel"]
           [button {:loading  saving?
                    :disabled (or saving? (not ready?))
                    :on-click submit}
            "Check connection & save"]])]]}]))


(defn- remove-dialog [{:keys [provider on-close on-confirm]}]
  [dialog/dialog
   {:open           (some? provider)
    :on-open-change (fn [open?] (when-not open? (on-close)))
    :title          (str "Remove " (byol/provider-name provider) " key?")
    :content        [:p {:class "text-sm text-kleene-text-secondary"}
                     "KAI stops using this provider. If it is your default provider, the default provider and model are cleared."]
    :x-close        false
    :actions        [:<>
                     [button {:variant :secondary :on-click on-close} "Cancel"]
                     [button {:variant :error :on-click on-confirm} "Remove"]]}])


(defn byol-section [{:keys [row settings]}]
  (let [[key-drawer set-drawer] (react/useState nil)
        [confirm set-confirm]   (react/useState nil)
        credentials           @(re-posh/subscribe [:ai-chat/provider-credentials])
        saving?               @(re-posh/subscribe [:mold-http/loading? :ai-chat/update-settings])
        {:ai-settings/keys [byol-enabled byol-effective default-provider default-model
                            allow-user-model-selection approved-models]} settings
        byol-enabled?         (boolean byol-enabled)
        loaded?               (some? credentials)
        provider-options      (byol/provider-options credentials)
        model-options         (byol/model-options credentials default-provider)
        model-offered?        (some #(= default-model (:value %)) model-options)
        approved-options      (byol/approved-model-options credentials)
        approved-disabled?    (or (not byol-enabled?) (not allow-user-model-selection)
                                  saving? (empty? approved-options))
        save-setting          (partial save-setting settings)
        close-drawer          (fn []
                                (set-drawer nil)
                                (dispatch [:ai-chat/clear-credential-save-result]))]
    (react/useEffect
     (fn []
       (dispatch [:ai-chat/fetch-provider-credentials])
       (fn []))
     #js [])

    [:<>
     [:section {:class "border-t mt-2"}
      (row {:icon     KeyRound
            :title    "Bring Your Own LLM"
            :badge    "Opt-in"
            :body     "Route KAI chat through your own OpenAI, Anthropic or Google account, under your own agreement with that provider."
            :note     (cond
                        (and byol-enabled? byol-effective default-model (seq model-options) (not model-offered?))
                        (str default-model " is no longer offered by the " (byol/provider-name default-provider)
                             " key — choose another default model.")

                        (and byol-enabled? byol-effective)
                        (str "Active — KAI is using " default-model " from " (byol/provider-name default-provider) ".")

                        (and byol-enabled? (seq provider-options))
                        "Not active yet — KAI is still answering chats with Kleene's default model. Choose a default provider and model below to switch, or turn off BYOL to stay on Kleene's default model."

                        byol-enabled?
                        "Not active yet — add a validated key, then choose a default provider and model."

                        :else
                        "Document search, embeddings and PII obfuscation stay on Kleene's infrastructure either way.")
            :checked  byol-enabled?
            :on-click (fn [e]
                        (.preventDefault e)
                        (when-not saving?
                          (save-setting {:ai-settings/byol-enabled (not byol-enabled?)}
                                        {:byol-enabled (not byol-enabled?)}
                                        {:event (:byol/toggled posthog-events/events)
                                         :props {:enabled                    (not byol-enabled?)
                                                 :providers_configured_count (when loaded? (count provider-options))}})))
            :disabled false})

      [:div {:class "ml-14 mb-6 pl-4 border-l"}
       [typography/h3 "Providers"]
       (when-not byol-enabled?
         [:p {:class "mt-1 text-xs text-kleene-text-secondary"} "Enable BYOL to add provider keys."])
       [:div {:class "mt-2 divide-y divide-solid border rounded-md px-4"}
        (for [provider byol/providers]
          ^{:key (:id provider)}
          [provider-row {:provider      provider
                         :credential    (byol/credential-for credentials (:id provider))
                         :loaded?       loaded?
                         :byol-enabled? byol-enabled?
                         :on-add        #(set-drawer {:provider (:id provider)
                                                      :replace? (some? (byol/credential-for credentials (:id provider)))})
                         :on-remove     #(set-confirm {:remove (:id provider)})}])]
       [:div {:class (str "grid gap-4 mt-4 max-w-md"
                          (when-not byol-enabled? " opacity-40 pointer-events-none"))}
        [fields/select {:id          :default-provider
                        :label       "Select provider"
                        :native      false
                        :placeholder "Select provider…"
                        :disabled    (or (not byol-enabled?) saving? (empty? provider-options))
                        :options     provider-options
                        :value       (option-value provider-options default-provider)
                        :on-change   #(save-setting {:ai-settings/default-provider %
                                                     :ai-settings/default-model    nil
                                                     :ai-settings/byol-effective   false}
                                                    {:default-provider %}
                                                    {:event (:byol/default-provider-changed posthog-events/events)
                                                     :props {:from_provider default-provider
                                                             :to_provider   %}})
                        :helper      "Only providers with a validated key are listed."}]
        [fields/select {:id          :default-model
                        :label       "Select default model"
                        :native      false
                        :placeholder "Select model…"
                        :disabled    (or (not byol-enabled?) saving? (nil? default-provider) (empty? model-options))
                        :options     model-options
                        :value       (option-value model-options default-model)
                        :on-change   #(let [approved (byol/with-default-approved approved-models default-provider %)]
                                        (save-setting {:ai-settings/default-model % :ai-settings/approved-models approved}
                                                      {:default-model % :approved-models approved}
                                                      {:event (:byol/default-model-changed posthog-events/events)
                                                       :props {:from_model default-model
                                                               :to_model   %
                                                               :provider   default-provider}}))
                        :helper      "Models reported by the default provider when its key was validated."}]]]

      [:div {:class "border-t"}
       (row {:icon     SlidersHorizontal
             :title    "Let users pick a model"
             :badge    "Opt-in"
             :body     "If off, everyone uses the default model. If on, users can choose any approved model per conversation."
             :note     "Only approved models can be chosen; the server enforces the list."
             :checked  (boolean allow-user-model-selection)
             :on-click (fn [e]
                         (.preventDefault e)
                         (when-not saving?
                           (save-setting {:ai-settings/allow-user-model-selection (not allow-user-model-selection)}
                                         {:allow-user-model-selection (not allow-user-model-selection)}
                                         {:event (:byol/model-picker-toggled posthog-events/events)
                                          :props {:enabled               (not allow-user-model-selection)
                                                  :approved_models_count (count approved-models)}})))
             :disabled (not byol-enabled?)})]

      [:div {:class "ml-14 mb-6 pl-4 border-l"}
       (when (and byol-enabled? (not allow-user-model-selection))
         [:p {:class "mb-2 text-xs text-kleene-text-secondary"}
          "Turn on \"Let users pick a model\" to choose which models users see."])
       [:div {:class (str "max-w-md"
                          (when approved-disabled? " opacity-40 pointer-events-none"))}
        [fields/select {:id        :approved-models
                        :label     "Select models"
                        :multiple  true
                        :disabled  approved-disabled?
                        :options   approved-options
                        :value     (byol/approved-values approved-models)
                        :on-change #(let [models (byol/with-default-approved (byol/->approved-models %)
                                                                             default-provider default-model)
                                          change (byol/approved-models-change approved-models models)]
                                      (save-setting {:ai-settings/approved-models models}
                                                    {:approved-models models}
                                                    (when (or (seq (:models_added change)) (seq (:models_removed change)))
                                                      {:event (:byol/approved-models-updated posthog-events/events)
                                                       :props change})))
                        :helper    "Choose the models users can pick in chat. The default model is always included."}]]]]

     (when key-drawer
       ^{:key (:provider key-drawer)}
       [:f> credential-drawer (assoc key-drawer :on-close close-drawer)])

     [remove-dialog {:provider   (:remove confirm)
                     :on-close   #(set-confirm nil)
                     :on-confirm (fn []
                                   (let [provider (:remove confirm)]
                                     (dispatch [:ai-chat/delete-provider-credential provider
                                                {:event (:byol/key-removed posthog-events/events)
                                                 :props {:provider             provider
                                                         :was_default_provider (= provider default-provider)}}]))
                                   (set-confirm nil))}]]))
